import api from '@/api';
import { AxiosError } from 'axios';
import { updateToken } from '@/api/backend/_http';
import { BROKER_ID, BROKER_TOKEN } from '@/helpers/const';
import type { BrokerCurrentUserResponse } from '@/api/backend/brokers/user';
import { useCookies } from '@vueuse/integrations/useCookies';

const cookies = useCookies();

/** Backend responses that mean the token itself is no longer usable. */
const STALE_SESSION_ERRORS = ['invalid-jwt-token-error', 'auth-error'];

export type LiveBroker = BrokerCurrentUserResponse['broker'];

/**
 * Distinguishes "this token is dead" from "the request failed". Only the former
 * may discard the session — a network drop or a backend 500 must leave a
 * genuinely signed-in broker's cookies alone.
 * @param error - The rejection from an authenticated request.
 * @returns `true` when the backend rejected the token itself.
 */
const isStaleSessionError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }
  const status = error.response?.status;
  if (status === 401 || status === 403) {
    return true;
  }
  // The broker guard reports an unverifiable token as 400 `invalid-jwt-token-error`.
  return status === 400 && STALE_SESSION_ERRORS.includes(error.response?.data?.message);
};

/**
 * Every domain the current host could be holding a cookie against, most
 * specific first (`uat.apply.bridgit.com.au` → itself, `apply.bridgit.com.au`,
 * `bridgit.com.au`, `com.au`).
 *
 * A cookie can only be removed by matching the domain it was set with, and the
 * broker portal scopes `broker-token` to a shared parent domain so this app can
 * read it. Rather than duplicating the portal's domain derivation (which would
 * silently drift from it), try them all: the browser ignores cookie writes
 * against a public suffix, so the extra attempts are harmless no-ops.
 * @returns The candidate cookie domains for the current hostname.
 */
const candidateCookieDomains = (): string[] => {
  const parts = window.location.hostname.toLowerCase().split('.').filter(Boolean);
  const domains: string[] = [];
  for (let i = 0; i <= parts.length - 2; i++) {
    domains.push(parts.slice(i).join('.'));
  }
  return domains;
};

/**
 * Drops the broker session cookies and the API client's token so the current
 * visitor is treated as an unauthenticated customer.
 *
 * `broker-token` is a session cookie with no max-age while the JWT inside it
 * expires after `JWT_EXPIRES_IN`. Browsers that restore session cookies keep
 * serving the dead token for weeks, so a stale cookie has to be removed rather
 * than left to fail every subsequent request.
 * @returns {void}
 */
export const clearBrokerSession = (): void => {
  [BROKER_TOKEN, BROKER_ID].forEach((key) => {
    cookies.remove(key, { path: '/' });
    candidateCookieDomains().forEach((domain) => {
      cookies.remove(key, { path: '/', domain: `.${domain}` });
    });
  });
  updateToken(null);
};

/**
 * Persists a broker token that arrived via query string (e.g. a deep link to
 * `/application?broker=...`) into the `BROKER_TOKEN` cookie.
 *
 * Layouts that resume a saved draft on mount redirect via `router.push` without
 * forwarding the current query string, which drops `route.query.broker` before
 * `isBroker`'s cookie fallback is read. Calling this first keeps that fallback
 * populated so broker state survives the redirect.
 * @param queryBroker - The raw `route.query.broker` value.
 * @returns {void}
 */
export const persistBrokerTokenFromQuery = (queryBroker: unknown): void => {
  if (typeof queryBroker === 'string' && queryBroker) {
    cookies.set(BROKER_TOKEN, queryBroker, { path: '/' });
  }
};

/**
 * Confirms the token currently applied to the API client still resolves to a
 * broker, so callers can fall back to the customer journey instead of letting a
 * broker-guarded call reject mid-flow.
 *
 * Apply the candidate token with `updateToken()` before calling this. A token
 * the backend rejects is cleared; any other failure (offline, 5xx) leaves the
 * cookies in place and is simply reported as "unverified" for this page load.
 * @returns The authenticated broker, or `null` when there is no live session.
 */
export const resolveLiveBroker = async (): Promise<LiveBroker | null> => {
  try {
    const { data } = await api.backend.brokers.user.currentUser();
    if (data?.broker) {
      return data.broker;
    }
  } catch (error) {
    if (isStaleSessionError(error)) {
      console.error('Broker session has expired, clearing it:', api.getErrorMsg(error));
      clearBrokerSession();
      return null;
    }
    console.error('Could not verify the broker session:', api.getErrorMsg(error));
    return null;
  }
  // A 200 without a broker means the token authenticated as something else.
  clearBrokerSession();
  return null;
};

/**
 * Boolean form of {@link resolveLiveBroker} for callers that only need to know
 * whether the broker journey may be entered.
 * @returns `true` when the applied token belongs to a live broker session.
 */
export const hasLiveBrokerSession = async (): Promise<boolean> => (await resolveLiveBroker()) !== null;
